import { useState } from "react";
import { Plus, GripVertical, Pencil, Trash2, X, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useGroupStages, type GroupStage } from "@/hooks/useGroupStages";

const STAGE_COLORS = [
  { name: "Azul", value: "bg-blue-500" },
  { name: "Verde", value: "bg-green-500" },
  { name: "Amarelo", value: "bg-yellow-500" },
  { name: "Laranja", value: "bg-orange-500" },
  { name: "Vermelho", value: "bg-red-500" },
  { name: "Roxo", value: "bg-purple-500" },
  { name: "Rosa", value: "bg-pink-500" },
  { name: "Ciano", value: "bg-cyan-500" },
  { name: "Cinza", value: "bg-gray-500" },
  { name: "Índigo", value: "bg-indigo-500" },
];

interface GroupStageManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupId: string | null;
  groupName?: string;
}

export function GroupStageManagerDialog({
  open,
  onOpenChange,
  groupId,
  groupName,
}: GroupStageManagerDialogProps) {
  const { stages, loading, createStage, updateStage, deleteStage } = useGroupStages(groupId);
  const [isCreating, setIsCreating] = useState(false);
  const [newStageName, setNewStageName] = useState("");
  const [newStageColor, setNewStageColor] = useState(STAGE_COLORS[0].value);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");

  const handleCreate = async () => {
    if (!newStageName.trim()) return;

    const result = await createStage({
      name: newStageName.trim(),
      color: newStageColor,
    });

    if (result) {
      setNewStageName("");
      setNewStageColor(STAGE_COLORS[0].value);
      setIsCreating(false);
    }
  };

  const handleStartEdit = (stage: GroupStage) => {
    setEditingId(stage.id);
    setEditName(stage.name);
    setEditColor(stage.color);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editName.trim()) return;

    const result = await updateStage(editingId, {
      name: editName.trim(),
      color: editColor,
    });

    if (result) {
      setEditingId(null);
      setEditName("");
      setEditColor("");
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditColor("");
  };

  const handleDelete = async (id: string) => {
    await deleteStage(id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Colunas do Grupo</DialogTitle>
          <DialogDescription>
            Gerencie as colunas do Kanban para {groupName || "este grupo"}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Lista de estágios */}
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {loading ? (
              <div className="text-center py-4 text-muted-foreground">
                Carregando...
              </div>
            ) : stages.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                Nenhuma coluna cadastrada.
              </div>
            ) : (
              stages.map((stage) => (
                <div
                  key={stage.id}
                  className="flex items-center gap-2 p-2 rounded-lg border bg-card"
                >
                  {editingId === stage.id ? (
                    // Modo de edição
                    <div className="flex-1 space-y-2">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="h-8"
                        autoFocus
                      />
                      <div className="flex flex-wrap gap-1.5">
                        {STAGE_COLORS.map((color) => (
                          <button
                            key={color.value}
                            type="button"
                            onClick={() => setEditColor(color.value)}
                            className={cn(
                              "w-5 h-5 rounded-full transition-all",
                              color.value,
                              editColor === color.value
                                ? "ring-2 ring-offset-2 ring-primary"
                                : "hover:scale-110"
                            )}
                            title={color.name}
                          />
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleSaveEdit} className="h-7">
                          <Check className="w-3 h-3 mr-1" />
                          Salvar
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleCancelEdit}
                          className="h-7"
                        >
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Modo de visualização
                    <>
                      <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />
                      <div className={cn("w-3 h-3 rounded-full shrink-0", stage.color)} />
                      <span className="flex-1 text-sm font-medium truncate">
                        {stage.name}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleStartEdit(stage)}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => handleDelete(stage.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Criar novo estágio */}
          {isCreating ? (
            <div className="space-y-2 p-3 rounded-lg border bg-muted/30">
              <Input
                placeholder="Nome da coluna"
                value={newStageName}
                onChange={(e) => setNewStageName(e.target.value)}
                className="h-8"
                autoFocus
              />
              <div className="flex flex-wrap gap-1.5">
                {STAGE_COLORS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setNewStageColor(color.value)}
                    className={cn(
                      "w-5 h-5 rounded-full transition-all",
                      color.value,
                      newStageColor === color.value
                        ? "ring-2 ring-offset-2 ring-primary"
                        : "hover:scale-110"
                    )}
                    title={color.name}
                  />
                ))}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleCreate}
                  disabled={!newStageName.trim()}
                  className="h-7"
                >
                  <Check className="w-3 h-3 mr-1" />
                  Criar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setIsCreating(false);
                    setNewStageName("");
                  }}
                  className="h-7"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              onClick={() => setIsCreating(true)}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Coluna
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
